import { TableColumnType } from 'src/types/table';
import { ProductType } from 'src/types/product';

/** Flat catalog fields copied from nested PRODUCT onto a detail-table row. */
export type ProductCatalogFlatFields = {
    PRODUCT_ID?: number;
    EAN?: string | null;
    PRODUCT_TYPE?: string | null;
    WEIGHT?: number | null;
    HS_CODE?: string | null;
    AGECHECK?: number;
    DESCRIPTION?: string | null;
    EXP_METHOD?: string | null;
    INSERT_TYPE?: string | null;
    ROP?: number;
    EXPIRATION_DAYS?: number;
    SCAN_IMEI_SN_TYPE?: string;
    SN_REGEX?: string;
    WIDTH?: number | null;
    HEIGHT?: number | null;
    LENGTH?: number | null;
    PRICE?: number | null;
    UNIT_PRICE?: string | null;
    BATTERY_TYPE?: string | null;
    CUSTOM_FIELD_1?: string | null;
    CUSTOM_FIELD_2?: string | null;
    CUSTOM_FIELD_3?: string | null;
    ACTIVE?: number;
    CREATED_AT?: string;
    UPDATED_AT?: string;
    IS_DELETED?: number;
    DELETED_AT?: string | null;
};

const CATALOG_KEYS: (keyof ProductCatalogFlatFields)[] = [
    'EAN',
    'PRODUCT_TYPE',
    'WEIGHT',
    'HS_CODE',
    'AGECHECK',
    'DESCRIPTION',
    'EXP_METHOD',
    'INSERT_TYPE',
    'ROP',
    'EXPIRATION_DAYS',
    'SCAN_IMEI_SN_TYPE',
    'SN_REGEX',
    'WIDTH',
    'HEIGHT',
    'LENGTH',
    'PRICE',
    'UNIT_PRICE',
    'BATTERY_TYPE',
    'CUSTOM_FIELD_1',
    'CUSTOM_FIELD_2',
    'CUSTOM_FIELD_3',
    'ACTIVE',
    'CREATED_AT',
    'UPDATED_AT',
    'IS_DELETED',
    'DELETED_AT',
];

/**
 * Flatten catalog fields from nested PRODUCT onto the row.
 * Never maps product.ID → ID (return line ID must stay intact).
 * Uses PRODUCT_ID for the product identity column.
 */
export const flattenProductCatalogFields = (
    product?: Partial<ProductType> | Record<string, unknown> | null
): ProductCatalogFlatFields => {
    if (!product) {
        return {};
    }
    const flat: ProductCatalogFlatFields = {};
    const id = (product as Partial<ProductType>).ID;
    if (id != null) {
        flat.PRODUCT_ID = id;
    }
    for (const key of CATALOG_KEYS) {
        const value = (product as Record<string, unknown>)[key];
        if (value !== undefined) {
            (flat as Record<string, unknown>)[key] = value instanceof Date ? value.toISOString() : value;
        }
    }
    return flat;
};

/** Catalog columns available in the column picker (defaultShow controlled per entity). */
export const getProductCatalogColumns = (
    defaultShowKeys: string[] = []
): TableColumnType[] => {
    const show = new Set(defaultShowKeys);
    const col = (key: string, label: string, extra: Partial<TableColumnType> = {}): TableColumnType => ({
        key,
        label,
        defaultShow: show.has(key),
        ...extra,
    });

    return [
        col('PRODUCT_ID', 'id', { type: 'id' }),
        col('EAN', 'EAN'),
        col('PRODUCT_TYPE', 'productType', {
            type: 'select',
            selectOption: 'productTypeCodes',
        }),
        col('WEIGHT', 'weight', { type: 'number-filter' }),
        col('HS_CODE', 'hsCode'),
        col('AGECHECK', 'agecheck', { type: 'select', selectOption: 'boolNumberCodes' }),
        col('DESCRIPTION', 'description'),
        col('EXP_METHOD', 'expeditionMethod', {
            type: 'select',
            selectOption: 'expeditionMethodCodes',
        }),
        col('INSERT_TYPE', 'insertType', {
            type: 'select',
            selectOption: 'productInsetTypeCodes',
        }),
        col('ROP', 'ROP', { type: 'number-filter' }),
        col('EXPIRATION_DAYS', 'EXPIRATION_DAYS', { type: 'number-filter' }),
        col('SCAN_IMEI_SN_TYPE', 'scanSerialNumber', {
            type: 'select',
            selectOption: 'scanImeiSnCodes',
        }),
        col('SN_REGEX', 'snRegex'),
        col('WIDTH', 'width', { type: 'number-filter' }),
        col('HEIGHT', 'height', { type: 'number-filter' }),
        col('LENGTH', 'length', { type: 'number-filter' }),
        col('PRICE', 'catalogPrice', { type: 'number-filter' }),
        col('UNIT_PRICE', 'unitPrice', { type: 'select', selectOption: 'currencyCodes' }),
        col('BATTERY_TYPE', 'batteryType', {
            type: 'select',
            selectOption: 'batteryTypeCodes',
        }),
        col('CUSTOM_FIELD_1', 'customField1'),
        col('CUSTOM_FIELD_2', 'customField2'),
        col('CUSTOM_FIELD_3', 'customField3'),
        col('ACTIVE', 'active', { type: 'select', selectOption: 'boolNumberCodes' }),
        col('CREATED_AT', 'createdAt', { type: 'dateTime' }),
        col('UPDATED_AT', 'updatedAt', { type: 'dateTime' }),
        col('IS_DELETED', 'isDeleted', { type: 'select', selectOption: 'boolNumberCodes' }),
        col('DELETED_AT', 'deletedAt', { type: 'dateTime' }),
    ];
};

/** Keys of catalog columns that are off by default on all three detail tables. */
export const CATALOG_OFF_BY_DEFAULT_KEYS = [
    'AGECHECK',
    'DESCRIPTION',
    'EXP_METHOD',
    'INSERT_TYPE',
    'ROP',
    'EXPIRATION_DAYS',
    'SCAN_IMEI_SN_TYPE',
    'SN_REGEX',
    'WIDTH',
    'HEIGHT',
    'LENGTH',
    'PRICE',
    'UNIT_PRICE',
    'BATTERY_TYPE',
    'CUSTOM_FIELD_1',
    'CUSTOM_FIELD_2',
    'CUSTOM_FIELD_3',
    'ACTIVE',
    'CREATED_AT',
    'UPDATED_AT',
    'IS_DELETED',
    'DELETED_AT',
];

export const computeOrderProductTotalPrice = (
    unitPrice: number | null | undefined,
    count: number | null | undefined
): number | null => {
    if (unitPrice == null || count == null) {
        return null;
    }
    return Number(unitPrice) * Number(count);
};
